/**
 * ¿Está «Mensajes» disponible? (docs/29). Una sola consulta compartida a `GET /estado` del hub,
 * repetida cada 60 s mientras haya alguien mirando (el botón de la barra, los «Enviar»).
 *
 * Interruptor de seguridad: si el hub todavía no tiene Mensajes (404/503), no hay red o no hay
 * sesión del hub, `disponible` queda en false y TODOS los botones nuevos se esconden. Así esta
 * parte puede salir antes que la del hub sin mostrar nada roto.
 */
import { useEffect } from 'react';
import { create } from 'zustand';
import { hubPageUrl, irAlHub } from '../api/hub.js';
import { obtenerEstado, type EstadoMensajes } from '../api/mensajes.js';
import { embebidoEnHub } from './embebidoEnHub.js';

export const SONDEO_MS = 60_000;

export interface Disponibilidad {
  disponible: boolean;
  estado: EstadoMensajes | null;
}

/** Una consulta: cualquier falla (404/503, red, sin sesión) = no disponible. */
export async function consultarDisponibilidad(
  consultar: () => Promise<EstadoMensajes> = () => obtenerEstado(),
): Promise<Disponibilidad> {
  try {
    return { disponible: true, estado: await consultar() };
  } catch {
    return { disponible: false, estado: null };
  }
}

interface MensajesState extends Disponibilidad {
  consultado: boolean;
  refrescar: () => Promise<void>;
}

export const useMensajesStore = create<MensajesState>((set) => ({
  disponible: false,
  estado: null,
  consultado: false,
  refrescar: async () => {
    const d = await consultarDisponibilidad();
    set({ ...d, consultado: true });
  },
}));

let suscritos = 0;
let timer: ReturnType<typeof setInterval> | null = null;

/** Prende el sondeo (uno solo por pestaña aunque haya varios botones). Devuelve cómo apagarlo. */
export function suscribirSondeo(intervaloMs: number = SONDEO_MS): () => void {
  suscritos += 1;
  if (suscritos === 1) {
    void useMensajesStore.getState().refrescar();
    timer = setInterval(() => void useMensajesStore.getState().refrescar(), intervaloMs);
  }
  let activo = true;
  return () => {
    if (!activo) return;
    activo = false;
    suscritos -= 1;
    if (suscritos === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Solo pruebas. */
export function _resetMensajes(): void {
  if (timer) clearInterval(timer);
  timer = null;
  suscritos = 0;
  useMensajesStore.setState({ disponible: false, estado: null, consultado: false });
}

/** Estado de Mensajes con sondeo mientras el componente esté montado. */
export function useMensajes(): MensajesState {
  useEffect(() => suscribirSondeo(), []);
  return useMensajesStore();
}

export type PestanaMensajes = 'bandeja' | 'canales' | 'preferencias';

/**
 * Abre el panel de Mensajes del hub. Embebida: se lo pide al hub (`opus-hub:ir`). En su propio
 * sitio: `nuevaPestana` (desde un diálogo, para no perder lo escrito) o en esta misma pestaña.
 */
export function abrirPanelMensajes(
  tab?: PestanaMensajes,
  { nuevaPestana = false, enHub = embebidoEnHub() }: { nuevaPestana?: boolean; enHub?: boolean } = {},
): void {
  if (!enHub && nuevaPestana) {
    window.open(hubPageUrl('mensajes', tab), '_blank', 'noopener');
    return;
  }
  irAlHub('mensajes', enHub, tab);
}
